import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from "axios";
import './ResetPassword.css';

const ResetPassword = () => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Fetch password requirements on component mount
  useEffect(() => {
    const fetchPasswordRequirements = async () => {
      try {
        const response = await axios.get('https://lionfish-app-w73zu.ondigitalocean.app/api/password-requirements/');
        if (response.data.success) {
          setPasswordRequirements(response.data.requirements);
        }
      } catch (error) {
        console.error("Error fetching password requirements:", error);
        // Set default requirements as fallback
        setPasswordRequirements({
          min_length: 8,
          rules: [
            { type: 'min_length', message: 'Your password must contain at least 8 characters.' },
            { type: 'similarity', message: 'Your password cannot be too similar to your other personal information.' },
            { type: 'common', message: 'Your password cannot be a commonly used password.' },
            { type: 'numeric', message: 'Your password cannot be entirely numeric.' }
          ]
        });
      }
    };

    fetchPasswordRequirements();
  }, []);

  // Validate password requirements in real-time
  const validatePasswordRequirements = (password) => {
    if (!passwordRequirements) return {};

    const validation = {
      min_length: password.length >= passwordRequirements.min_length,
      not_numeric: !/^\d+$/.test(password),
      not_common: true, // Basic check - backend will do full validation
      not_similar: true // Will be validated by backend
    };

    // Check for common passwords (basic check)
    const commonPasswords = ['password', '12345678', 'qwerty', 'password123', 'admin'];
    validation.not_common = !commonPasswords.some(common =>
      password.toLowerCase().includes(common)
    );

    return validation;
  };

  const passwordValidation = validatePasswordRequirements(newPassword);
  const allRequirementsMet = newPassword.length > 0 &&
    Object.values(passwordValidation).every(val => val === true);

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match!");
      setLoading(false);
      return;
    }

    if (!allRequirementsMet) {
      setMessage("Please ensure your password meets all requirements.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('https://lionfish-app-w73zu.ondigitalocean.app/api/password_reset/confirm/', {
        token: token,
        password: newPassword,
      });

      setMessage("Password reset successfully! Redirecting to login...");
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error("Reset password error:", error);

      // Handle validation errors from backend
      if (error.response?.data?.password) {
        const errors = Array.isArray(error.response.data.password)
          ? error.response.data.password.join(' ')
          : error.response.data.password;
        setMessage(errors);
      } else {
        setMessage(error.response?.data?.error || "Error resetting password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <form onSubmit={handleReset} className="reset-password-form">
        <h2>Reset Your Password</h2>

        <div className="form-group">
          <label>Reset Token</label>
          <input
            type="text"
            placeholder="Enter token from email"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>New Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={passwordRequirements?.min_length || 8}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "👁️" : "👁️‍️"}
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        {passwordRequirements && newPassword.length > 0 && (
          <div className="password-requirements">
            <h4>Password Requirements:</h4>
            <ul className="requirements-list">
              <li className={passwordValidation.min_length ? 'met' : 'unmet'}>
                <span className="requirement-icon">
                  {passwordValidation.min_length ? '✓' : '○'}
                </span>
                At least {passwordRequirements.min_length} characters
              </li>
              <li className={passwordValidation.not_numeric ? 'met' : 'unmet'}>
                <span className="requirement-icon">
                  {passwordValidation.not_numeric ? '✓' : '○'}
                </span>
                Not entirely numeric
              </li>
              <li className={passwordValidation.not_common ? 'met' : 'unmet'}>
                <span className="requirement-icon">
                  {passwordValidation.not_common ? '✓' : '○'}
                </span>
                Not a common password
              </li>
              <li className={passwordValidation.not_similar ? 'met' : 'unmet'}>
                <span className="requirement-icon">
                  {passwordValidation.not_similar ? '✓' : '○'}
                </span>
                Not too similar to personal info
              </li>
            </ul>
          </div>
        )}

        {/* Show all requirements before user starts typing */}
        {passwordRequirements && newPassword.length === 0 && (
          <div className="password-requirements initial">
            <h4>Password must:</h4>
            <ul className="requirements-list">
              {passwordRequirements.rules.map((rule, index) => (
                <li key={index} className="unmet">
                  <span className="requirement-icon">○</span>
                  {rule.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="form-group">
          <label>Confirm Password</label>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <span className="validation-error">Passwords do not match</span>
          )}
          {confirmPassword && newPassword === confirmPassword && (
            <span className="validation-success">✓ Passwords match</span>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !token || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          className={allRequirementsMet ? 'enabled' : 'disabled'}
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>

        {message && (
          <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </form>

      <p className="login-link">
        Remembered your password? <Link to="/login">Login</Link>
      </p>
    </div>
  );
};

export default ResetPassword;
